import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Wand2, AlertTriangle, Camera, Type, Link2, Bell,
  Loader2, Upload, CheckCircle, XCircle, Info, ArrowRight,
  Mic, MicOff, Copy, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toneOptions = [
  { value: "professional", label: "Professional", desc: "Formal and business-appropriate" },
  { value: "friendly", label: "Friendly", desc: "Warm and approachable" },
  { value: "firm", label: "Firm", desc: "Direct and assertive" },
  { value: "polite", label: "Polite", desc: "Courteous and diplomatic" },
  { value: "legal", label: "Legal", desc: "Formal legal language" },
];

export default function AITools() {
  const navigate = useNavigate();
  
  // Auto-Fill state
  const [autoFillText, setAutoFillText] = useState("");
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState(null);
  
  // OCR state
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const ocrInputRef = useRef(null);
  
  // Tone Rewrite state
  const [toneText, setToneText] = useState("");
  const [selectedTone, setSelectedTone] = useState("professional");
  const [toneLoading, setToneLoading] = useState(false);
  const [rewrittenText, setRewrittenText] = useState("");
  const [copied, setCopied] = useState(false);

  // Auto-Fill handler
  const handleAutoFill = async () => {
    if (!autoFillText.trim()) {
      toast.error("Please enter some text");
      return;
    }
    
    setAutoFillLoading(true);
    setAutoFillResult(null);
    
    try {
      const response = await axios.post(`${API}/ai/auto-fill`, { text: autoFillText });
      
      if (response.data.success) {
        setAutoFillResult(response.data);
        toast.success(`Detected: ${response.data.doc_type.replace("-", " ")}`);
      } else {
        toast.error(response.data.message || "Could not parse input");
      }
    } catch (error) {
      toast.error("Auto-fill failed");
      console.error(error);
    } finally {
      setAutoFillLoading(false);
    }
  };

  const createFromAutoFill = () => {
    if (!autoFillResult) return;
    
    // Store data in sessionStorage for the document editor
    sessionStorage.setItem("autoFillData", JSON.stringify(autoFillResult.data));
    navigate(`/tools/${autoFillResult.doc_type}`);
  };

  // OCR handler
  const handleOCR = async () => {
    if (!ocrFile) {
      toast.error("Please upload an image");
      return;
    }
    
    setOcrLoading(true);
    setOcrResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", ocrFile);
      
      const response = await axios.post(`${API}/ai/ocr-receipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        setOcrResult(response.data);
        toast.success("Receipt scanned successfully!");
      } else {
        toast.error(response.data.message || "Could not scan receipt");
      }
    } catch (error) {
      toast.error("OCR failed");
      console.error(error);
    } finally {
      setOcrLoading(false);
    }
  };

  const createFromOCR = (docType) => {
    if (!ocrResult) return;
    
    const data = {
      recipient: { name: ocrResult.vendor },
      items: ocrResult.items.length > 0 ? ocrResult.items : [{ description: "From receipt", amount: ocrResult.amount }],
      totals: { total: ocrResult.amount },
      meta: { issue_date: ocrResult.date }
    };
    
    sessionStorage.setItem("autoFillData", JSON.stringify(data));
    navigate(`/tools/${docType}`);
  };

  // Tone Rewrite handler
  const handleToneRewrite = async () => {
    if (!toneText.trim()) {
      toast.error("Please enter some text");
      return;
    }
    
    setToneLoading(true);
    setRewrittenText("");
    
    try {
      const response = await axios.post(`${API}/ai/rewrite-tone`, {
        text: toneText,
        tone: selectedTone
      });
      
      if (response.data.success) {
        setRewrittenText(response.data.rewritten_text);
        toast.success("Text rewritten!");
      } else {
        toast.error(response.data.message || "Rewrite failed");
      }
    } catch (error) {
      toast.error("Rewrite failed");
      console.error(error);
    } finally {
      setToneLoading(false);
    }
  };

  const copyRewrittenText = () => {
    navigator.clipboard.writeText(rewrittenText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="max-w-4xl mx-auto animate-slide-in" data-testid="ai-tools-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
          <Wand2 className="w-4 h-4" />
          <span>AI-Powered Tools</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">
          Smart Document Assistant
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
          Use AI to auto-fill forms, scan receipts, rewrite text, and more
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="autofill" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="autofill" data-testid="tab-autofill">
            <Wand2 className="w-4 h-4 mr-2" /> Auto-Fill
          </TabsTrigger>
          <TabsTrigger value="ocr" data-testid="tab-ocr">
            <Camera className="w-4 h-4 mr-2" /> Scan Receipt
          </TabsTrigger>
          <TabsTrigger value="tone" data-testid="tab-tone">
            <Type className="w-4 h-4 mr-2" /> Tone Rewrite
          </TabsTrigger>
        </TabsList>

        {/* Auto-Fill Tab */}
        <TabsContent value="autofill">
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-lg">Plain-English Auto-Fill</h2>
                <p className="text-sm text-muted-foreground">
                  Describe your document in plain English and AI will fill in all the fields
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Describe your document</Label>
                <Textarea
                  value={autoFillText}
                  onChange={(e) => setAutoFillText(e.target.value)}
                  placeholder="Example: Create an invoice for John Doe at Acme Inc, web design services for $750, due January 15, paid via Cash App"
                  rows={4}
                  className="mt-1"
                  data-testid="autofill-input"
                />
              </div>

              <Button
                onClick={handleAutoFill}
                disabled={autoFillLoading || !autoFillText.trim()}
                className="w-full"
                data-testid="autofill-btn"
              >
                {autoFillLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Auto-Fill Document
                  </>
                )}
              </Button>

              {/* Auto-Fill Result */}
              {autoFillResult && (
                <div className="mt-6 p-4 bg-muted/50 rounded-lg" data-testid="autofill-result">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="font-medium">
                      Detected: {autoFillResult.doc_type.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {autoFillResult.data.recipient?.name && (
                      <p><span className="text-muted-foreground">Recipient:</span> {autoFillResult.data.recipient.name}</p>
                    )}
                    {autoFillResult.data.items?.length > 0 && (
                      <p><span className="text-muted-foreground">Items:</span> {autoFillResult.data.items.map(i => i.description).join(", ")}</p>
                    )}
                    {autoFillResult.data.total > 0 && (
                      <p><span className="text-muted-foreground">Total:</span> ${autoFillResult.data.total}</p>
                    )}
                    {autoFillResult.data.due_date && (
                      <p><span className="text-muted-foreground">Due:</span> {autoFillResult.data.due_date}</p>
                    )}
                  </div>

                  <Button
                    onClick={createFromAutoFill}
                    className="w-full mt-4 bg-secondary hover:bg-secondary/90"
                    data-testid="create-from-autofill"
                  >
                    Create {autoFillResult.doc_type.replace("-", " ")} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* OCR Tab */}
        <TabsContent value="ocr">
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Camera className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-lg">Receipt Scanner (OCR)</h2>
                <p className="text-sm text-muted-foreground">
                  Upload a photo of a receipt and AI will extract all the details
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Upload Area */}
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 text-center ${
                  ocrFile ? "border-secondary/50 bg-secondary/5" : "border-border hover:border-secondary/50"
                }`}
              >
                <input
                  ref={ocrInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && setOcrFile(e.target.files[0])}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="ocr-file-input"
                />
                
                {ocrFile ? (
                  <div>
                    <Camera className="w-10 h-10 text-secondary mx-auto mb-2" />
                    <p className="font-medium">{ocrFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(ocrFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="font-medium">Drop receipt image here</p>
                    <p className="text-sm text-muted-foreground">or click to browse</p>
                  </div>
                )}
              </div>

              <Button
                onClick={handleOCR}
                disabled={ocrLoading || !ocrFile}
                className="w-full bg-secondary hover:bg-secondary/90"
                data-testid="ocr-scan-btn"
              >
                {ocrLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" />
                    Scan Receipt
                  </>
                )}
              </Button>

              {/* OCR Result */}
              {ocrResult && (
                <div className="mt-6 p-4 bg-muted/50 rounded-lg" data-testid="ocr-result">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="font-medium">Receipt Scanned</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-muted-foreground">Vendor</p>
                      <p className="font-medium">{ocrResult.vendor || "Unknown"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Date</p>
                      <p className="font-medium">{ocrResult.date || "Unknown"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Amount</p>
                      <p className="font-medium text-lg">${ocrResult.amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Category</p>
                      <p className="font-medium capitalize">{ocrResult.category}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => createFromOCR("receipt")}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      Create Receipt
                    </Button>
                    <Button
                      onClick={() => createFromOCR("expense-claim")}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      Create Expense Claim
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Tone Rewrite Tab */}
        <TabsContent value="tone">
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Type className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-lg">Tone Rewriting</h2>
                <p className="text-sm text-muted-foreground">
                  Rewrite your text to match the perfect tone for your document
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Your text</Label>
                <Textarea
                  value={toneText}
                  onChange={(e) => setToneText(e.target.value)}
                  placeholder="Example: Pay this invoice immediately or else."
                  rows={3}
                  className="mt-1"
                  data-testid="tone-input"
                />
              </div>

              <div>
                <Label>Select tone</Label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {toneOptions.map((tone) => (
                    <button
                      key={tone.value}
                      onClick={() => setSelectedTone(tone.value)}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        selectedTone === tone.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`tone-${tone.value}`}
                    >
                      <p className="text-sm font-medium">{tone.label}</p>
                      <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{tone.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleToneRewrite}
                disabled={toneLoading || !toneText.trim()}
                className="w-full"
                data-testid="tone-rewrite-btn"
              >
                {toneLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Rewriting...
                  </>
                ) : (
                  <>
                    <Type className="w-4 h-4 mr-2" />
                    Rewrite Text
                  </>
                )}
              </Button>

              {/* Rewritten Result */}
              {rewrittenText && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg" data-testid="tone-result">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Rewritten ({selectedTone})
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyRewrittenText}
                      className="h-7"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{rewrittenText}</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
